import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { ProofingScope } from '../editor/spellingPlugin.ts';
import { WritingClock, formatClock, wordCount } from '@realmery/realm-core';
import type { MentionCandidate } from '../editor/mentionPicker.ts';
import { RichTextEditor } from '../editor/RichTextEditor.tsx';

/**
 * Story Studio's focus mode (docs/PARITY-STEP-5-PLAN.md decision 4), as the desktop's
 * `FocusWriter`: the scene's text alone, in the browser's full screen, as a large column
 * with no toolbar; the word count and the scene's goal, the writing timer and Done (Esc) in
 * a quiet bar at the bottom. The timer stops counting once nothing has been typed for the
 * minutes set in Settings > Writing, and starts again at the next key. Leaving hands the
 * sitting back for the writing log.
 */

export interface Sitting {
  readonly html: string;
  readonly started: string;
  readonly ended: string;
  readonly activeSeconds: number;
  readonly wordsStart: number;
  readonly wordsEnd: number;
}

export function FocusMode({
  title,
  html,
  wordGoal,
  idleMinutes,
  mentionCandidates,
  proofing,
  onChange,
  onDone,
}: {
  /** Spelling, remembered for the scene. */
  proofing?: ProofingScope;
  title: string;
  html: string;
  wordGoal: number;
  idleMinutes: number;
  mentionCandidates: readonly MentionCandidate[];
  onChange: (html: string) => void;
  onDone: (sitting: Sitting) => void;
}) {
  const page = useRef<HTMLDivElement>(null);
  const [start] = useState(() => ({ at: new Date().toISOString(), words: wordCount(html) }));
  const [clock] = useState(() => new WritingClock(idleMinutes * 60_000, Date.now()));
  const latest = useRef(html);
  const [words, setWords] = useState(start.words);
  const [, setNow] = useState(0);
  const finished = useRef(false);

  const done = () => {
    if (finished.current) return;
    finished.current = true;
    clock.tick(Date.now());
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    onDone({ html: latest.current, started: start.at, ended: new Date().toISOString(), activeSeconds: clock.activeSeconds, wordsStart: start.words, wordsEnd: wordCount(latest.current) });
  };
  const doneRef = useRef(done);
  doneRef.current = done;

  useEffect(() => {
    const element = page.current!;
    let entered = false;
    // Full screen where the browser allows it; otherwise the page alone fills the window.
    element.requestFullscreen?.().then(
      () => {
        entered = true;
      },
      () => undefined,
    );
    // Leaving full screen the browser's way (Esc, F11) leaves focus mode too.
    const onFullscreen = () => {
      if (entered && !document.fullscreenElement) doneRef.current();
    };
    document.addEventListener('fullscreenchange', onFullscreen);
    const timer = setInterval(() => {
      clock.tick(Date.now());
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
      document.removeEventListener('fullscreenchange', onFullscreen);
    };
  }, [clock]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Esc leaves, before the editor's own keys see it, unless it's closing the mention list
    // (the editor names that list while it's open); every other key is writing.
    const picking = event.target instanceof Element && event.target.closest('[aria-autocomplete]') !== null;
    if (event.key === 'Escape' && !picking) {
      event.preventDefault();
      done();
    } else clock.activity(Date.now());
  }

  const idle = clock.isIdle(Date.now());
  return (
    <div ref={page} className="focus-mode" role="dialog" aria-modal="true" aria-label={`Focus: ${title}`} onKeyDownCapture={onKeyDown}>
      <div className="focus-page">
        <RichTextEditor
          label={`${title}: the manuscript`}
          initialHtml={html}
          toolbar={false}
          autoFocus
          mentionCandidates={mentionCandidates}
          proofing={proofing}
          onChange={(next) => {
            latest.current = next;
            setWords(wordCount(next));
            onChange(next);
          }}
        />
      </div>
      <div className="focus-bar">
        <span>{wordGoal > 0 ? `${words.toLocaleString('en')} / ${wordGoal.toLocaleString('en')} words` : `${words.toLocaleString('en')} words`}</span>
        {wordGoal > 0 && <progress max={wordGoal} value={Math.min(words, wordGoal)} aria-label="Progress toward the scene's word goal" />}
        <span className="focus-spacer" />
        <span
          className={idle ? 'focus-timer focus-timer-paused' : 'focus-timer'}
          title={`Time spent writing since focus mode opened. It stops counting after ${idleMinutes} minute${idleMinutes === 1 ? '' : 's'} without typing, and starts again when you type.`}
        >
          {formatClock(clock.activeSeconds)}
          {idle ? ' paused' : ''}
        </span>
        <button type="button" onClick={done} title="Leave focus mode and go back to the scene (Esc)">
          Done (Esc)
        </button>
      </div>
    </div>
  );
}
